import { useQuery } from '@tanstack/react-query'
import { MessagesSquare } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { useAuth } from '@/features/auth/useAuth'
import { conversationsQuery } from '@/features/messaging/api'
import { ConversationList } from '@/features/messaging/components/ConversationList'
import { NewConversationDialog } from '@/features/messaging/components/NewConversationDialog'
import { Thread } from '@/features/messaging/components/Thread'
import { messagesBase } from '@/features/messaging/format'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/utils/cn'

/** Messages: conversations on the left, the open one on the right (one pane at a time on phones). */
export function MessagesPage() {
  const { t } = useTranslation('messaging')
  useDocumentTitle('Messages')
  const { portal, user } = useAuth()
  const params = useParams()
  const activeId = Number(params.conversationId) || null
  const base = messagesBase(portal)
  const list = useQuery(conversationsQuery())
  const [creating, setCreating] = useState(false)
  const canGroup = user !== null && !['student', 'parent'].includes(user.role)

  return (
    <div className="-mx-4 -my-6 sm:-mx-6 lg:-mx-8 lg:-my-8">
      <div className="flex h-[calc(100dvh-var(--spacing-header))] min-h-[28rem] overflow-hidden border-line bg-surface lg:border-l">
        <aside
          className={cn(
            'w-full shrink-0 border-r border-line lg:block lg:w-80 xl:w-96',
            activeId !== null ? 'hidden' : 'block',
          )}
          aria-label={t('Conversations')}
        >
          {list.isPending ? (
            <LoadingState />
          ) : list.error ? (
            <ErrorState onRetry={() => void list.refetch()} />
          ) : (
            <ConversationList
              rows={list.data}
              activeId={activeId}
              base={base}
              onNew={() => setCreating(true)}
            />
          )}
        </aside>
        <section className={cn('min-w-0 flex-1', activeId === null ? 'hidden lg:block' : 'block')}>
          {activeId !== null ? (
            <Thread key={activeId} id={activeId} backHref={base} />
          ) : (
            <div className="grid h-full place-items-center p-6">
              <EmptyState
                icon={<MessagesSquare />}
                title={t('Your messages')}
                description={t(
                  'Choose a conversation, or start a new one. Class rooms appear here for your classes.',
                )}
              />
            </div>
          )}
        </section>
      </div>
      <NewConversationDialog open={creating} onOpenChange={setCreating} base={base} canGroup={canGroup} />
    </div>
  )
}
